define('drops/calendar/calendarItems',[
    'jquery-ui',
    'underscore',
    'luxon',
    'when',
    'lq/style',
    'drops/calendar/calendarParameter',
    'lq/onlongpress'
], function (ui, _, luxon, when, Style, calendarParameter, OnLongPress) {
    'use strict';

    const { Interval } = luxon;

    const minutesPerDay = 1440;

    var calendarItemsCollide = function (item, otherItem) {
        return item.data.endOfItem > otherItem.data.startOfItem
            && item.data.startOfItem < otherItem.data.endOfItem;
    };

    var getNumberOfAvailableColumns = function (item, columns, columnIndexOfItem) {
        var availableColumns = 1;

        for (var i = columnIndexOfItem + 1; i < columns.length; i++) {
            var column = columns[i];

            for (var j = 0; j < column.length; j++) {
                var otherItem = column[j];

                if (calendarItemsCollide(item, otherItem))
                    return availableColumns;
            }

            availableColumns++;
        }

        return availableColumns;
    };

    var layoutGroupOfCalendarItems = function (columns, layoutMode) {
        var isHorizontal = layoutMode === 'horizontal';
        for (var i = 0; i < columns.length; i++) {
            var column = columns[i];

            for (var j = 0; j < column.length; j++) {
                var item = column[j];
                var colspan = getNumberOfAvailableColumns(item, columns, i);

                if (isHorizontal) {
                    // Planning view: overlapping items stack vertically with fixed pixel height per slot
                    item.overlapLayout = { columnIndex: i, colspan: colspan };
                } else {
                    // Day/Week view: overlapping items stack horizontally (left/width)
                    item.$element.css({
                        'left': ((i / columns.length) * 100) + '%',
                        'width': ((100 / columns.length) * colspan) + '%'
                    });
                }
            }
        }
    };

    var startNewGroupOfCalendarItems = function (lastEncounteredItem, nextItem) {
        return !!lastEncounteredItem && lastEncounteredItem.data.endOfItem < nextItem.data.startOfItem;
    };

    var sortCalendarItems = function (items) {
        return items.sort(function (firstItem, secondItem) {
            if (firstItem.data.startOfItem < secondItem.data.startOfItem) return -1;
            if (firstItem.data.startOfItem > secondItem.data.startOfItem) return 1;
            if (firstItem.data.endOfItem < secondItem.data.endOfItem) return -1;
            if (firstItem.data.endOfItem > secondItem.data.endOfItem) return 1;
            return 0;
        });
    };

    var tryAddingItemToExistingColumn = function (columns, item) {
        for (var i = 0; i < columns.length; i++) {
            var column = columns[i];

            if (!calendarItemsCollide(_.last(column), item)) {
                column.push(item);
                return true;
            }
        }

        return false;
    };

    var layoutOverlappingCalendarItems = function (items) {
        var columns = [];
        var lastEncounteredItem = null;
        var layoutMode = items.length && items[0].data.layoutMode || 'vertical';
        var maxColumns = 1;

        _.each(sortCalendarItems(items), function (item) {
            if (startNewGroupOfCalendarItems(lastEncounteredItem, item)) {
                maxColumns = Math.max(maxColumns, columns.length);
                layoutGroupOfCalendarItems(columns, layoutMode);

                columns = [];
                lastEncounteredItem = null;
            }

            if (!tryAddingItemToExistingColumn(columns, item))
                columns.push([item]);

            if (lastEncounteredItem === null || item.data.endOfItem > lastEncounteredItem.data.endOfItem)
                lastEncounteredItem = item;
        });

        maxColumns = Math.max(maxColumns, columns.length);
        if (columns.length > 0)
            layoutGroupOfCalendarItems(columns, layoutMode);

        return maxColumns;
    };

    var handleItemSelection = function (calendarView, drop) {
        new OnLongPress().applyLongPressOnElement(drop.$element, drop, null,
            function () {
                if (drop !== calendarView.selectedItem) {
                    calendarView.selectedItem = drop;
                    drop.$element.addClass('selected')
                }
                drop.triggerEventIfEnabled('clicked');
            });

        if (drop.data.eventing.clicked.enabled) {
            drop.$element.addClass('clickable');
        }
    };

    var makeResizableAndDraggable = function (drop, drops, calendarView, resizable, isMultiweek, direction) {
        if (!drop.isEventEnabled('onUpdate')) return;

        var timeslotSize = calendarView.data.timeslotSize; // in minutes
        var timeslotHeight = getTimeslotHeight(calendarView);
        var timeslotWidth = getTimeslotWidth(calendarView);

        // Defines the snap interval, defaults to timeslotSize
        var snapInterval = drop.data.resizeSnapInterval ? drop.data.resizeSnapInterval : timeslotSize;
        var snapHeight = (timeslotHeight / timeslotSize) * snapInterval;
        var snapWidth = timeslotWidth ? (timeslotWidth / timeslotSize) * snapInterval : snapHeight;

        var weekdayWidth = drop.data.$target.parent().outerWidth();
        
        var resizeDirection;

        var isWeekView = function () { return calendarView.type === "CalendarWeekView"; }
        var isDayView = function () { return calendarView.type === "CalendarDayView"; }
        var isHorizontal = isPlanningView(calendarView);
        var hasPlanningGroups = isHorizontal && calendarView.groups && calendarView.groups.length > 1;

        var getGroup = function () {
            var groups = calendarView.data.calendarItemGroups || [];
            var group = _.find(groups, function (g) {
                return g.groupMembers && g.groupMembers.some(function (m) { return m === drop; });
            });
            if (!group) {
                group = _.find(groups, function (g) {
                    var idOut = drop.data.idOutput;
                    if (!idOut || !drop.data.row) return false;
                    var dropId = drop.data.row[idOut];
                    var memberId = g.groupMembers[0] && g.groupMembers[0].data.row && g.groupMembers[0].data.row[idOut];
                    return dropId === memberId;
                });
            }
            return group || { groupMembers: [drop] };
        }
        
        var onStart = function (event, ui) {
            setOtherItemsOpacity(0.6);
            if (hasPlanningGroups && drop.$element) {
                drop.$element.css('position', 'fixed');
                calendarView.dragCurrentContainer = drop.data.$target;
                if (calendarView.$container && calendarView.$container.length && drop.data.$target && drop.data.$target.length) {
                    var e = event.originalEvent;
                    if (e && e.clientX != null) {
                        var $scroll = calendarView.$container;
                        var scrollRect = $scroll[0].getBoundingClientRect();
                        var scrollLeft = $scroll[0].scrollLeft;
                        var itemRect = drop.$element[0].getBoundingClientRect();
                        var mouseInContent = (e.clientX - scrollRect.left) + scrollLeft;
                        var itemLeftInContent = (itemRect.left - scrollRect.left) + scrollLeft;
                        calendarView.dragStartClickOffsetX = mouseInContent - itemLeftInContent;
                        calendarView.dragStartContainerLeft = itemLeftInContent;
                    }
                }
                if (drop.data.$target && drop.data.$target.length && calendarView.dragStartContainerLeft === undefined) {
                    var startRect = drop.data.$target[0].getBoundingClientRect();
                    calendarView.dragStartContainerLeft = ui.position.left - startRect.left;
                    calendarView.dragStartContainerTop = ui.position.top - startRect.top;
                }
                if (drop.data.$target && drop.data.$target.length) {
                    var startRect = drop.data.$target[0].getBoundingClientRect();
                    calendarView.dragStartContainerTop = ui.position.top - startRect.top;
                }
            }
            if (isDayView() && calendarView.$container && calendarView.$container.length && drop.data.$target && drop.data.$target.length
                && !$(event.target).closest('.ui-resizable-handle').length) {
                var eDay = event.originalEvent;
                if (eDay && eDay.clientY != null) {
                    var $scrollDay = calendarView.$container;
                    var scrollRectDay = $scrollDay[0].getBoundingClientRect();
                    var scrollTopDay = $scrollDay[0].scrollTop;
                    var itemRectDay = drop.$element[0].getBoundingClientRect();
                    var mouseInContentY = (eDay.clientY - scrollRectDay.top) + scrollTopDay;
                    var itemTopInContent = (itemRectDay.top - scrollRectDay.top) + scrollTopDay;
                    calendarView.dragStartClickOffsetY = mouseInContentY - itemTopInContent;
                    calendarView.dragStartContainerTop = itemTopInContent;
                    calendarView.dragStartItemCssTop = parseFloat(drop.$element.css('top')) || 0;
                    var gmDay = getGroup().groupMembers;
                    calendarView.dragStartVerticalGroupTops = _.map(gmDay, function (m) {
                        return parseFloat(m.$element.css('top')) || 0;
                    });
                }
            }
            // Save the direction of the resize, it's important that this is in the onStart method rather than the onStop
            if (event.originalEvent.target.className.includes("ui-resizable-n")) {
                resizeDirection = "n";
            } else if (event.originalEvent.target.className.includes("ui-resizable-s")) {
                resizeDirection = "s";
            } else if (event.originalEvent.target.className.includes("ui-resizable-e")) {
                resizeDirection = "e";
            } else if (event.originalEvent.target.className.includes("ui-resizable-w")) {
                resizeDirection = "w";
            }
        }
        
        var onStop = function (event, ui) {
            snapDropPosition();

            // Groups hold all elements of a timespan entry
            // E.g. in a week view when an item spans multiple columns (days) the group holds all item 'fragments' of that entry
            // Meaning if the timespan exists only on a single day earlyDrop and lateDrop will be the same
            var group = getGroup().groupMembers;

            var earlyDrop = group[0]; // Represents the earliest entry in the group
            var lateDrop = group[group.length - 1]; // Represents the latest entry in the group

            if (event.type.includes('drag')) { // The element was dragged
                drop.cancelOtherEvents = true;
                drop.parent.cancelOtherEvents = true;

                // Calculate the amount of time the user has resized
                var timeInMinutes;
                if (isHorizontal) {
                    var positionDelta;
                    if (hasPlanningGroups && calendarView.dragStartContainerLeft !== undefined) {
                        var finalContainerLeft;
                        if (calendarView.$container && calendarView.$container.length) {
                            var $scroll = calendarView.$container;
                            var scrollRect = $scroll[0].getBoundingClientRect();
                            var scrollLeft = $scroll[0].scrollLeft;
                            var elementRect = drop.$element[0].getBoundingClientRect();
                            finalContainerLeft = (elementRect.left - scrollRect.left) + scrollLeft;
                        } else if (calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length) {
                            var endRect = calendarView.dragCurrentContainer[0].getBoundingClientRect();
                            finalContainerLeft = drop.$element[0].getBoundingClientRect().left - endRect.left;
                        }
                        if (finalContainerLeft !== undefined) {
                            positionDelta = finalContainerLeft - calendarView.dragStartContainerLeft;
                        }
                    }
                    if (positionDelta === undefined) {
                        positionDelta = ui.position.left - ui.originalPosition.left;
                    }
                    let timeslotsDragged = Math.round(positionDelta / snapWidth);
                    timeInMinutes = timeslotsDragged * snapInterval;
                } else {
                    var positionDeltaY;
                    if (isDayView() && calendarView.dragStartContainerTop !== undefined && calendarView.$container && calendarView.$container.length) {
                        var $scrollY = calendarView.$container;
                        var scrollRectY = $scrollY[0].getBoundingClientRect();
                        var scrollTopY = $scrollY[0].scrollTop;
                        var elementRectY = drop.$element[0].getBoundingClientRect();
                        var finalContainerTop = (elementRectY.top - scrollRectY.top) + scrollTopY;
                        positionDeltaY = finalContainerTop - calendarView.dragStartContainerTop;
                    }
                    if (positionDeltaY === undefined) {
                        positionDeltaY = ui.position.top - ui.originalPosition.top;
                    }
                    let timeslotsDragged = Math.round(positionDeltaY / snapHeight);
                    timeInMinutes = timeslotsDragged * snapInterval;
                }

                earlyDrop.data.startOfItem = earlyDrop.data.startOfItem.plus({ minutes: timeInMinutes });
                lateDrop.data.endOfItem = lateDrop.data.endOfItem.plus({ minutes: timeInMinutes });

                if (isWeekView()) { // if the item is dragged to another day in case of the weekview
                    var daysDragged = Math.round((ui.position.left - ui.originalPosition.left) / weekdayWidth);
                    
                    earlyDrop.data.startOfItem = earlyDrop.data.startOfItem.plus({ days: daysDragged });
                    lateDrop.data.endOfItem = lateDrop.data.endOfItem.plus({ days: daysDragged });

                    // Reduntant information, but some part of the code still relies on it
                    earlyDrop.data.startOfDay = earlyDrop.data.startOfDay.plus({ days: daysDragged });
                    lateDrop.data.endOfDay = lateDrop.data.endOfDay.plus({ days: daysDragged });
                }
            } else { // The element was resized
                // Calculate the amount of time the user has resized
                var timeInMinutes;
                if (isHorizontal) {
                    var timeslotsResized = Math.round((ui.size.width - ui.originalSize.width) / snapWidth);
                    timeInMinutes = timeslotsResized * snapInterval;
                    if (resizeDirection === "w") {
                        earlyDrop.data.startOfItem = earlyDrop.data.startOfItem.minus({ minutes: timeInMinutes });
                    } else if (resizeDirection === "e") {
                        lateDrop.data.endOfItem = lateDrop.data.endOfItem.plus({ minutes: timeInMinutes });
                    }
                } else {
                    var timeslotsResized = Math.round((ui.size.height - ui.originalSize.height) / snapHeight);
                    timeInMinutes = timeslotsResized * snapInterval;
                    if (resizeDirection === "n") {
                        earlyDrop.data.startOfItem = earlyDrop.data.startOfItem.minus({ minutes: timeInMinutes });
                    } else if (resizeDirection === "s") {
                        lateDrop.data.endOfItem = lateDrop.data.endOfItem.plus({ minutes: timeInMinutes });
                    }
                }
            }

            setOtherItemsOpacity(1);
            if (hasPlanningGroups && drop.$element) {
                drop.$element.css('position', '');
                delete calendarView.dragCurrentContainer;
                delete calendarView.dragStartContainerLeft;
                delete calendarView.dragStartContainerTop;
                delete calendarView.dragStartClickOffsetX;
            }
            if (isDayView()) {
                delete calendarView.dragStartClickOffsetY;
                delete calendarView.dragStartContainerTop;
                delete calendarView.dragStartItemCssTop;
                delete calendarView.dragStartVerticalGroupTops;
            }

            var newGroupId = undefined;
            if (isHorizontal) {
                if (hasPlanningGroups && calendarView.$planningGroups) {
                    var e = event.originalEvent;
                    var pointX = e && (e.clientX !== undefined) ? e.clientX : (drop.$element.offset().left + drop.$element.outerWidth() / 2);
                    var pointY = e && (e.clientY !== undefined) ? e.clientY : (drop.$element.offset().top + drop.$element.outerHeight() / 2);
                    drop.$element.css('pointer-events', 'none');
                    var $elementAt = $(document.elementFromPoint(pointX, pointY));
                    drop.$element.css('pointer-events', '');
                    var $targetGroup = $elementAt.closest('.planning-group');
                    newGroupId = $targetGroup.length ? $targetGroup.data('groupId') : null;
                    var newContainer = $targetGroup.length ? $targetGroup.find('.calendar-item-container') : null;
                    var oldContainer = drop.data.$target;
                    if (!newContainer || !newContainer.length) {
                        newContainer = oldContainer;
                    }
                    if (newContainer && newContainer.length) {
                        drop.$element.appendTo(newContainer);
                    }
                    if (newContainer && newContainer.length && oldContainer && oldContainer[0] !== newContainer[0]) {
                        var oldGroupId = drop.data.groupId;
                        drop.data.groupId = newGroupId;
                        drop.data.$target = newContainer;
                        if (calendarView.data.groupOutput && drop.data.row) {
                            drop.data.row[calendarView.data.groupOutput] = newGroupId;
                        }
                        drop.applyPosition();
                        var oldContainerDrops = drops.filter(function (d) {
                            return d.data.$target && d.data.$target[0] === oldContainer[0];
                        });
                        var oldMaxCols = oldContainerDrops.length ? layoutOverlappingCalendarItems(oldContainerDrops) : 1;
                        oldContainer.attr('data-max-overlap-columns', oldMaxCols);
                        _.each(oldContainerDrops, function (d) { d.applyPosition(); });
                    }
                }
                var containerDrops = drops.filter(function (d) {
                    return d.data.groupId == drop.data.groupId || (d.data.groupId == null && drop.data.groupId == null);
                });
                if (containerDrops.length && containerDrops[0].data.$target) {
                    var maxCols = layoutOverlappingCalendarItems(containerDrops);
                    containerDrops[0].data.$target.attr('data-max-overlap-columns', maxCols);
                    calendarView.updatePlanningViewRowHeights();
                    _.each(containerDrops, function (d) { d.applyPosition(); });
                }
            }

            var id = drop.data.row[drop.data.idOutput];
            const params = drop.data.updateParams;
            if (params) {
                if (params.newStart) calendarView.piping.parameters.locals.setValue(params.newStart, calendarParameter.convert(earlyDrop.data.startOfItem));
                if (params.newEnd) calendarView.piping.parameters.locals.setValue(params.newEnd, calendarParameter.convert(lateDrop.data.endOfItem));
                if (params.id) calendarView.piping.parameters.locals.setValue(params.id, id);
                if (params.newGroup) calendarView.piping.parameters.locals.setValue(params.newGroup, newGroupId);
            }
            triggerCalendarItemEventWithWaitOverlay(calendarView, drop, 'onUpdate');
        }

        if (resizable) {
            var safeParseFloat = function(val) {
                return parseFloat(val) || 0;
            }

            const itemPaddingY = safeParseFloat(drop.$element.css('padding-top')) + safeParseFloat(drop.$element.css('padding-bottom'));
            const itemBorderY = safeParseFloat(drop.$element.css('border-top-width')) + safeParseFloat(drop.$element.css('border-bottom-width'));
            const itemPaddingX = safeParseFloat(drop.$element.css('padding-left')) + safeParseFloat(drop.$element.css('padding-right'));
            const itemBorderX = safeParseFloat(drop.$element.css('border-left-width')) + safeParseFloat(drop.$element.css('border-right-width'));

            // Prevent duplicate initialization
            if (drop.$element.is('.ui-resizable')) {
                direction = drop.$element.resizable("option", "handles") + ', ' + direction;
                drop.$element.resizable("destroy");
            }

            drop.$element.resizable({
                handles: direction,
                grid: isHorizontal ? [snapWidth, 0] : [0, snapHeight],
                containment: "parent",
                start: onStart,
                stop: onStop,
                resize: function (_event, ui) {
                    if (isHorizontal) {
                        ui.position.top = ui.originalPosition.top;
                        ui.position.left = round(ui.position.left, snapWidth);
                        // Necessary adjustment because JQuery UI has problems with decimal values that made the element width 'jittery'
                        ui.size.width = ui.size.width - (itemPaddingX + itemBorderX);

                        // The resize is invalid when it reaches another same day element 
                        var invalidResize = checkForOverlappingHorizontal(drop, ui.position.left, ui.size.width);

                        if (invalidResize && calendarView.data.savedResizePosition) {
                            ui.position.left = calendarView.data.savedResizePosition.uiLeft;
                            ui.size.width = calendarView.data.savedResizePosition.uiWidth;
                        }

                        calendarView.data.savedResizePosition = {
                            uiLeft: ui.position.left,
                            uiWidth: ui.size.width,
                        };
                    } else {
                        ui.position.left = ui.originalPosition.left;
                        ui.position.top = round(ui.position.top, snapHeight);
                        ui.size.height = ui.size.height - (itemPaddingY + itemBorderY);

                        var invalidResize = checkForOverlappingVertical(drop, ui.position.top, 0, ui.size.height);

                        if (invalidResize && calendarView.data.savedResizePosition) {
                            ui.position.top = calendarView.data.savedResizePosition.uiTop;
                            ui.size.height = calendarView.data.savedResizePosition.uiHeight;
                        }

                        calendarView.data.savedResizePosition = {
                            uiTop: ui.position.top,
                            uiHeight: ui.size.height,
                        };
                    }
                }
            });
        }

        if (!isMultiweek) {
            drop.$element.draggable({
                axis: (isHorizontal && hasPlanningGroups) ? "x, y" : (isHorizontal ? "x" : (isWeekView() ? "x, y" : "y")),
                grid: (isHorizontal && hasPlanningGroups) ? [snapWidth, 1] : (isHorizontal ? [snapWidth, 0] : [weekdayWidth, snapHeight]),
                containment: isWeekView() ? ".weekdays" : (hasPlanningGroups && calendarView.$planningGroups && calendarView.$planningGroups[0] ? calendarView.$planningGroups[0] : "parent"),
                scroll: !hasPlanningGroups,
                scrollSensitivity: 100,
                scrollSpeed: 5,
                appendTo: hasPlanningGroups ? "body" : "parent",
                iframeFix: hasPlanningGroups,
                start: onStart,
                stop: onStop,
                drag: function (_event, ui) {
                    var groupMembers = getGroup().groupMembers;

                    // Planning view: scroll container when cursor near edges (like week view)
                    if (hasPlanningGroups && calendarView.$container && calendarView.$container.length) {
                        var e = _event.originalEvent;
                        if (e && e.clientX != null && e.clientY != null) {
                            var $scrollEl = calendarView.$container;
                            var el = $scrollEl[0];
                            var rect = el.getBoundingClientRect();
                            var scrollSens = 100;
                            var scrollSpd = 5;
                            var clientX = e.clientX;
                            var clientY = e.clientY;
                            if (clientY < rect.top + scrollSens && el.scrollTop > 0) {
                                el.scrollTop = Math.max(0, el.scrollTop - scrollSpd);
                            } else if (clientY > rect.bottom - scrollSens && el.scrollTop < el.scrollHeight - el.clientHeight) {
                                el.scrollTop = Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + scrollSpd);
                            }
                            if (clientX < rect.left + scrollSens && el.scrollLeft > 0) {
                                el.scrollLeft = Math.max(0, el.scrollLeft - scrollSpd);
                            } else if (clientX > rect.right - scrollSens && el.scrollLeft < el.scrollWidth - el.clientWidth) {
                                el.scrollLeft = Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + scrollSpd);
                            }
                        }
                    }

                    if (groupMembers.length > 1 && !hasPlanningGroups) {
                        drop.$element.draggable({ axis: "x" });
                    } else if (isHorizontal && !hasPlanningGroups) {
                        drop.$element.draggable({ axis: "x" });
                    } else if (isHorizontal && hasPlanningGroups) {
                        drop.$element.draggable({ axis: "x, y" });
                    } else if (!isWeekView()) {
                        drop.$element.draggable({ axis: "y" });
                    } else {
                        drop.$element.draggable({ axis: "x, y" });
                    }

                    if (isHorizontal) {
                        var containerLeft = ui.position.left;
                        var containerTop = ui.position.top;
                        if (hasPlanningGroups && calendarView.$container && calendarView.$container.length && calendarView.dragStartClickOffsetX !== undefined) {
                            var e = _event.originalEvent;
                            if (e && e.clientX != null) {
                                var $scroll = calendarView.$container;
                                var scrollRect = $scroll[0].getBoundingClientRect();
                                var scrollLeft = $scroll[0].scrollLeft;
                                containerLeft = (e.clientX - scrollRect.left) + scrollLeft - calendarView.dragStartClickOffsetX;
                            }
                        } else if (hasPlanningGroups && calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length) {
                            var containerRect = calendarView.dragCurrentContainer[0].getBoundingClientRect();
                            containerLeft = ui.position.left - containerRect.left;
                            containerTop = ui.position.top - containerRect.top;
                        }
                        containerLeft = Math.max(0, round(containerLeft, snapWidth));

                        drop.$element.css("left", getRoundedValueForProperty(drop, "left"));

                        // Vertical: switch container to target row so top:0 is always correct
                        if (hasPlanningGroups && calendarView.$planningGroups) {
                            var e = _event.originalEvent;
                            if (e && e.clientX != null) {
                                drop.$element.css('pointer-events', 'none');
                                var $under = $(document.elementFromPoint(e.clientX, e.clientY));
                                drop.$element.css('pointer-events', '');
                                var $targetGroup = $under.closest('.planning-group');
                                var $container = $targetGroup.length ? $targetGroup.find('.calendar-item-container') : null;
                                var $target = ($container && $container.length) ? $container : calendarView.dragCurrentContainer;
                                if ($target && $target.length && drop.$element.parent()[0] !== $target[0]) {
                                    drop.$element.appendTo($target);
                                    drop.$element.css('position', 'absolute');
                                    if ($container && $container.length) {
                                        calendarView.dragCurrentContainer = $container;
                                    }
                                }
                                containerTop = 0;
                                ui.position.top = 0;
                            }
                        }
                        if (hasPlanningGroups && drop.$element.parent()[0] !== document.body) {
                            containerTop = 0;
                        }

                        // The drag is invalid if the dragged item would overlap with another item
                        var invalidDrag = _.some(groupMembers, (function (groupMember) {
                            return checkForOverlappingHorizontal(groupMember, containerLeft, undefined);
                        }));

                        // If the drag is invalid use the previously saved position
                        if (invalidDrag && calendarView.data.savedDragPosition) {
                            containerLeft = calendarView.data.savedDragPosition.uiLeft;
                            if (hasPlanningGroups && calendarView.data.savedDragPosition.uiTop !== undefined) {
                                containerTop = calendarView.data.savedDragPosition.uiTop;
                            }
                            if (hasPlanningGroups && calendarView.$container && calendarView.$container.length) {
                                var $scroll = calendarView.$container;
                                var scrollRect = $scroll[0].getBoundingClientRect();
                                var scrollLeft = $scroll[0].scrollLeft;
                                var viewportLeft = (containerLeft - scrollLeft) + scrollRect.left;
                                drop.$element.css({
                                    left: viewportLeft + 'px',
                                    top: (containerTop + (calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length ? calendarView.dragCurrentContainer[0].getBoundingClientRect().top : 0)) + 'px'
                                });
                            } else if (hasPlanningGroups && calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length) {
                                var restoreRect = calendarView.dragCurrentContainer[0].getBoundingClientRect();
                                drop.$element.css({
                                    left: (containerLeft + restoreRect.left) + 'px',
                                    top: (containerTop + restoreRect.top) + 'px'
                                });
                            }
                        }

                        // Save the current position
                        calendarView.data.savedDragPosition = hasPlanningGroups
                            ? { uiLeft: containerLeft, uiTop: containerTop }
                            : { uiLeft: ui.position.left };

                        var uiLeft, uiTop;
                        if (hasPlanningGroups && calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length) {
                            uiLeft = containerLeft;
                            uiTop = 0; // Snap to top of row
                        }
                        if (uiLeft !== undefined && uiTop !== undefined) {
                            ui.position.left = uiLeft;
                            ui.position.top = uiTop;
                        }
                        
                        if (!invalidDrag) {
                            var membersToUpdate = hasPlanningGroups ? [drop] : groupMembers;
                            var itemHeight = hasPlanningGroups ? ((drop.data.size && drop.data.size.y != null) ? drop.data.size.y : timeslotHeight) : null;
                            var containerRect = hasPlanningGroups && calendarView.dragCurrentContainer && calendarView.dragCurrentContainer.length
                                ? calendarView.dragCurrentContainer[0].getBoundingClientRect() : null;
                            var leftVal = uiLeft !== undefined ? uiLeft : (hasPlanningGroups ? (containerLeft + (containerRect ? containerRect.left : 0)) : null);
                            var topVal = uiTop !== undefined ? uiTop : (hasPlanningGroups ? (containerTop + (containerRect ? containerRect.top : 0)) : null);
                            _.each(membersToUpdate, function (groupMember) {
                                var css = groupMember.$element.css(["top", "left", "width", "height"]);
                                var l = leftVal != null ? leftVal : (css.left + ui.position.left);
                                var t = topVal != null ? topVal : css.top;
                                groupMember.$element.css({
                                    top: t,
                                    left: l,
                                    width: css.width,
                                    height: hasPlanningGroups && itemHeight != null ? (itemHeight + 'px') : css.height
                                });
                            });
                        }
                    } else {
                        var cssTopForDrag;
                        if (isDayView() && calendarView.$container && calendarView.$container.length && calendarView.dragStartClickOffsetY !== undefined) {
                            var eVert = _event.originalEvent;
                            if (eVert && eVert.clientY != null && drop.data.$target && drop.data.$target.length) {
                                var $scrollV = calendarView.$container;
                                var scrollRectV = $scrollV[0].getBoundingClientRect();
                                var scrollTopV = $scrollV[0].scrollTop;
                                var containerTopScroll = (eVert.clientY - scrollRectV.top) + scrollTopV - calendarView.dragStartClickOffsetY;
                                var targetRectV = drop.data.$target[0].getBoundingClientRect();
                                var targetTopInScroll = (targetRectV.top - scrollRectV.top) + scrollTopV;
                                cssTopForDrag = round(containerTopScroll - targetTopInScroll, snapHeight);
                            }
                        }
                        if (cssTopForDrag === undefined) {
                            cssTopForDrag = round(ui.position.top, snapHeight);
                        }
                        ui.position.top = cssTopForDrag;
                        drop.$element.css("height", getRoundedValueForProperty(drop, "height"));

                        // The drag is invalid if the dragged item would overlap with another item
                        var invalidDrag = _.some(groupMembers, (function (groupMember) {
                            return checkForOverlappingVertical(groupMember, ui.position.top, ui.position.left - ui.originalPosition.left);
                        }));

                        // If the drag is invalid use the previously saved position
                        if (invalidDrag && calendarView.data.savedDragPosition) {
                            ui.position.top = calendarView.data.savedDragPosition.uiTop;
                            ui.position.left = calendarView.data.savedDragPosition.uiLeft;
                        }

                        // Save the current position
                        calendarView.data.savedDragPosition = {
                            uiTop: ui.position.top,
                            uiLeft: ui.position.left
                        };

                        if (!invalidDrag) {
                            if (isDayView() && calendarView.dragStartVerticalGroupTops && calendarView.dragStartItemCssTop !== undefined) {
                                var deltaTopDay = ui.position.top - calendarView.dragStartItemCssTop;
                                _.each(groupMembers, function (groupMember, idx) {
                                    var css = groupMember.$element.css(["top", "left", "width"]);
                                    var baseTop = calendarView.dragStartVerticalGroupTops[idx] != null ? calendarView.dragStartVerticalGroupTops[idx] : (parseFloat(css.top) || 0);
                                    groupMember.$element.css({
                                        top: (baseTop + deltaTopDay) + 'px',
                                        left: ui.position.left,
                                        width: css.width
                                    });
                                });
                            } else {
                                _.each(groupMembers, function (groupMember) {
                                    var css = groupMember.$element.css(["top", "left", "width"]);
                                    groupMember.$element.css({
                                        top: css.top + ui.position.top,
                                        left: ui.position.left,
                                        width: css.width
                                    });
                                });
                            }
                        }
                    }
                }
            });
        }

        var checkForOverlappingVertical = function (draggedDrop, top, left, height) {
            if (draggedDrop.data.allowItemOverlap) return false;

            // Get the current position and height
            const draggedDropCss = draggedDrop.$element.css(["top", "height"]);
            const draggedDropHeight = height || draggedDropCss.height;
            const draggedDropTop = top !== undefined ? top : draggedDropCss.top;

            // Find drops on the same day, excluding the dragged drop and its group members
            const groupMembers = getGroup().groupMembers;
            const daysDragged = Math.round(left / weekdayWidth);
            const currentDraggedDay = draggedDrop.data.startOfItem.day + daysDragged;

            const sameDayDrops = drops.filter(drop => (
                drop !== draggedDrop &&
                !groupMembers.includes(drop) &&
                currentDraggedDay === drop.data.startOfItem.day
            ));

            const draggedDropPosition = calculatePosition(parseInt(draggedDropTop), parseInt(draggedDropHeight), snapHeight);

            // Check for overlap with any same day drop
            return sameDayDrops.some(function (otherDrop) {
                var otherDropCss = otherDrop.$element.css(["top", "height"]);
                var otherDropPosition = calculatePosition(parseInt(otherDropCss.top), parseInt(otherDropCss.height), snapHeight);
                return doItemsOverlap(otherDropPosition, draggedDropPosition);
            });
        }

        var checkForOverlappingHorizontal = function (draggedDrop, posLeft, width) {
            if (draggedDrop.data.allowItemOverlap) return false;

            // Get the current position and width
            const draggedDropCss = draggedDrop.$element.css(["left", "width"]);
            const draggedDropWidth = width !== undefined ? width : draggedDropCss.width;
            const draggedDropLeft = posLeft !== undefined ? posLeft : draggedDropCss.left;

            // Find drops on the same day, excluding the dragged drop and its group members
            const groupMembers = getGroup().groupMembers;
            const daysDragged = hasPlanningGroups ? 0 : Math.round((posLeft || 0) / weekdayWidth);
            const currentDraggedDay = draggedDrop.data.startOfItem.day + daysDragged;
            
            var targetGroupId = draggedDrop.data.groupId;
            if (hasPlanningGroups && draggedDrop.$element) {
                var offset = draggedDrop.$element.offset();
                var cx = offset.left + draggedDrop.$element.outerWidth() / 2;
                var cy = offset.top + draggedDrop.$element.outerHeight() / 2;
                var $under = $(document.elementFromPoint(cx, cy));
                var $targetGroup = $under.closest('.planning-group');
                if ($targetGroup.length) targetGroupId = $targetGroup.data('groupId');
            }
            var checkGroupId = targetGroupId;
            const sameDayDrops = drops.filter(drop => (
                drop !== draggedDrop &&
                !groupMembers.includes(drop) &&
                currentDraggedDay === drop.data.startOfItem.day &&
                (checkGroupId == drop.data.groupId || (checkGroupId == null && drop.data.groupId == null))
            ));

            const draggedDropPosition = calculatePosition(parseInt(draggedDropLeft), parseInt(draggedDropWidth), snapWidth);

            // Check for overlap with any same day drop
            return sameDayDrops.some(function (otherDrop) {
                var otherDropCss = otherDrop.$element.css(["left", "width"]);
                var otherDropPosition = calculatePosition(parseInt(otherDropCss.left), parseInt(otherDropCss.width), snapWidth);
                return doItemsOverlap(otherDropPosition, draggedDropPosition);
            });
        }

        var calculatePosition = function (offset, size, snap) {
            return ({
                start: round(parseFloat(offset), snap),
                end: round(parseFloat(offset) + parseFloat(size), snap)
            });
        }

        var doItemsOverlap = function (pos1, pos2) {
            // Check for any vertical overlap
            const overlap = 
                (pos1.start < pos2.end && pos1.end > pos2.start) ||
                (pos2.start < pos1.end && pos2.end > pos1.start);

            // Also consider exact match as overlap
            const exactMatch = (pos1.start === pos2.start && pos1.end === pos2.end);

            return overlap || exactMatch;
        }

        var setOtherItemsOpacity = function (opacity) {
            var group = getGroup().groupMembers;
            _.each(drops, function (dropFromLoop) {
                if (dropFromLoop == drop || group.includes(dropFromLoop)) return;
                dropFromLoop.$element.css("opacity", opacity);
            });
        }

        var snapDropPosition = function () {
            if (isHorizontal) {
                drop.$element.css("width", getRoundedValueForProperty(drop, "width") + "px");
                drop.$element.css("left", getRoundedValueForProperty(drop, "left") + "px");
            } else {
                drop.$element.css("height", getRoundedValueForProperty(drop, "height") + "px");
                drop.$element.css("top", getRoundedValueForProperty(drop, "top") + "px");
            }
        }

        var getRoundedValueForProperty = function (forDrop, property) {
            var snap = isHorizontal ? snapWidth : snapHeight;
            return round(parseFloat(forDrop.$element.css(property)), snap);
        }
    };

    var getTimeslotHeight = function (calendarView) {
        if (isPlanningView(calendarView)) {
            var calendarItemTemplate = _.find(calendarView.childDrops, function (d) { return d.data && d.data.type === 'CalendarItem'; });
            if (calendarItemTemplate && calendarItemTemplate.data && calendarItemTemplate.data.size && calendarItemTemplate.data.size.y != null) {
                return calendarItemTemplate.data.size.y;
            }
            var $rowTimeslot = calendarView.$planningGroups && calendarView.$planningGroups.find('.timeslots .timeslot').first();
            if ($rowTimeslot && $rowTimeslot.length) {
                return parseFloat($rowTimeslot.css('height')) || 60;
            }
        }
        var viewClassSelector = "." + calendarView.$element.attr("class").replace(/ /g, ".");
        var timeslotHeight = parseFloat($(viewClassSelector + " .timeslot").css("height")); // px3
        return timeslotHeight;
    };

    var getTimeslotWidth = function (calendarView) {
        if (isPlanningView(calendarView)) {
            var $rowTimeslot = calendarView.$planningGroups && calendarView.$planningGroups.find('.timeslots > .timeslot').first();
            if ($rowTimeslot && $rowTimeslot.length) {
                var rectWidth = $rowTimeslot[0] && $rowTimeslot[0].getBoundingClientRect
                    ? $rowTimeslot[0].getBoundingClientRect().width
                    : 0;
                return rectWidth || $rowTimeslot.outerWidth() || parseFloat($rowTimeslot.css('width')) || 60;
            }
            var timeSlotItemTemplate = _.find(calendarView.childDrops, function (d) { return d.data && d.data.type === 'CalendarTimeSlotItem'; });
            if (timeSlotItemTemplate && timeSlotItemTemplate.data && timeSlotItemTemplate.data.size && timeSlotItemTemplate.data.size.x != null) {
                return timeSlotItemTemplate.data.size.x;
            }
        }
        var viewClassSelector = "." + calendarView.$element.attr("class").replace(/ /g, ".");
        var timeslotWidth = parseFloat($(viewClassSelector + " .timeslot").css("width"));
        return timeslotWidth;
    };

    var isPlanningView = function (calendarView) {
        return calendarView.type === "CalendarPlanningView";
    };

    var round = function (x, roundTo) {
        var rounded = Math.round(x / roundTo) * roundTo;
        if (rounded === minutesPerDay) {
            return rounded - 1;
        }
        return rounded;
    };

    var floor = function (x, roundTo) {
        return Math.floor(x / roundTo) * roundTo;
    };

    var getMouseEventMinutes = function (e, data, isMove, calendarView) {
        var calendarItemTemplate = calendarView && _.find(calendarView.childDrops, function (d) { return d.data && d.data.type === 'CalendarItem'; });
        var creationInterval = (calendarItemTemplate && calendarItemTemplate.data.resizeSnapInterval) || data.timeslotSizeterval;
        if (isPlanningView(data)) {
            var timeslotStartMinutes = $(e.target).data('time');
            if (timeslotStartMinutes == null) timeslotStartMinutes = 0;
            var slotWidth = e.target.offsetWidth || data.timeslotSize;
            var minutesWithinSlot = (e.offsetX / slotWidth) * data.timeslotSize;
            var xTot = timeslotStartMinutes + minutesWithinSlot;

            if (!!isMove) {
                return round(xTot, creationInterval);
            }
            return floor(xTot, creationInterval);
        } else {
            var timeslotIndex = $(e.target).index();
            var y = e.offsetY;
            var yTot = timeslotIndex * data.timeslotSize + y;

            if (!!isMove) {
                return round(yTot, creationInterval);
            }
            return floor(yTot, creationInterval);
        }
    };

    var fillUp = function (calendarView, itemsData) {
        calendarView.selectedItem = null;

        if (!itemsData.length) {
            if (isPlanningView(calendarView) && calendarView.$planningGroups) {
                calendarView.$planningGroups.find('.calendar-item-container').attr('data-max-overlap-columns', '1');
                calendarView.updatePlanningViewRowHeights();
            }
            return when();
        }

        var drops = calendarView.piping.dropFactory.createDrops(itemsData);

        _.each(drops, function (drop) {
            calendarView.addDrop(drop);
            handleItemSelection(calendarView, drop);
            drop.piping = calendarView.piping.fork(drop.data.row, calendarView, drop.data.idOutput);
        });

        var getContainerDrops = function (containerEl) {
            if (!containerEl) return [];
            return _.filter(calendarView.pool || [], function (d) {
                return d.type === 'CalendarItem' && d.data.$target && d.data.$target[0] === containerEl;
            });
        };

        var containersToUpdate = _.uniq(_.map(drops, function (d) { return d.data.$target && d.data.$target[0]; }), false, function (el) { return el; });
        _.each(containersToUpdate, function (containerEl) {
            if (!containerEl) return;
            var $target = $(containerEl);
            var containerDrops = getContainerDrops(containerEl);
            if (containerDrops.length) {
                var maxCols = layoutOverlappingCalendarItems(containerDrops);
                $target.attr('data-max-overlap-columns', maxCols);
                _.each(containerDrops, function (d) { d.applyPosition(); });
            }
        });

        var groupMembers = [];
        var calendarItemGroups = [];
        var ids = []

        _.each(drops, function (drop) {
            groupMembers = [];
            _.each(drops, function (d) {
                if (drop && d.data.row[d.data.idOutput] ===
                    drop.data.row[drop.data.idOutput]
                    && !ids.includes(d.data.row[drop.data.idOutput])
                    ) {
                    groupMembers.push(d);
                }
            });
            ids.push(drop.data.row[drop.data.idOutput]);

            if (groupMembers.length > 0)
                calendarItemGroups.push({ groupMembers: groupMembers });
        });

        _.each(calendarItemGroups, function (g) {
            var isMultiweek = false;
            if (calendarView.getStartOfCurrentWeek) {
                var startOfItem = calendarView.getDatetimeFromRow(g.groupMembers[0].data.row, calendarView.data.startOutput);
                var endOfItem = calendarView.getDatetimeFromRow(g.groupMembers[0].data.row, calendarView.data.endOutput);
                var startOfCurrentWeek = calendarView.getStartOfCurrentWeek();
                var endOfCurrentWeek = calendarView.getEndOfCurrentWeek();
                var currentWeekInterval = Interval.fromDateTimes(startOfCurrentWeek, endOfCurrentWeek);

                var startIsBetween = currentWeekInterval.contains(startOfItem);
                var endIsBetween = currentWeekInterval.contains(endOfItem);

                isMultiweek = !startIsBetween || !endIsBetween;

                for (var j = 1; j < g.groupMembers.length - 1; j++) {
                    makeResizableAndDraggable(g.groupMembers[j], drops, calendarView, false, isMultiweek);
                }
                if (startOfCurrentWeek <= startOfItem) {
                    makeResizableAndDraggable(g.groupMembers[0], drops, calendarView, true, isMultiweek, 'n');
                }
                if (endOfCurrentWeek >= endOfItem) {
                    makeResizableAndDraggable(g.groupMembers[g.groupMembers.length - 1], drops, calendarView, true, isMultiweek, 's');
                }
            } else {
                var resizeHandles = isPlanningView(calendarView) ? 'e, w' : 'n, s';
                makeResizableAndDraggable(g.groupMembers[0], drops, calendarView, true, false, resizeHandles);
            }

        });

        var existingGroups = calendarView.data.calendarItemGroups || [];
        var poolSet = {};
        _.each(calendarView.pool || [], function (p) { poolSet[p] = true; });
        var validExistingGroups = _.filter(existingGroups, function (g) {
            return g.groupMembers && _.every(g.groupMembers, function (m) { return poolSet[m]; });
        });
        var existingIds = _.flatten(_.map(validExistingGroups, function (g) {
            return (g.groupMembers || []).map(function (m) {
                return m.data && m.data.idOutput && m.data.row ? m.data.row[m.data.idOutput] : undefined;
            });
        }));
        var isAddingItems = drops.length > 0 && _.every(drops, function (d) {
            var idOut = d.data.idOutput;
            var id = idOut && d.data.row ? d.data.row[idOut] : undefined;
            return !id || !_.contains(existingIds, id);
        });
        calendarView.data.calendarItemGroups = isAddingItems ? validExistingGroups.concat(calendarItemGroups) : calendarItemGroups;

        return when.all(_.map(drops, function (drop) {
            return drop.render(drop.data.$target);
        })).then(function () {
            if (isPlanningView(calendarView)) {
                calendarView.updatePlanningViewRowHeights();
            }
            return drops;
        });
    };

    var ensureLoadingOverlay = function (calendarView) {
        if (!calendarView.$loadingOverlay) {
            var $parent = (calendarView.type === 'CalendarPlanningView' && calendarView.$container) ? calendarView.$container : calendarView.$element;
            calendarView.$loadingOverlay = $('<div class="calendar-loading-overlay hidden">')
                .append($('<div class="calendar-loading-spinner">'))
                .appendTo($parent);
        }
        return calendarView.$loadingOverlay;
    };

    var showLoadingOverlay = function (calendarView) {
        if (calendarView.designData) return;
        var $overlay = ensureLoadingOverlay(calendarView);
        if (calendarView.type === 'CalendarPlanningView' && calendarView.$hours && calendarView.$planningGroups) {
            $overlay.css({ top: '0', left: '0', right: '0', bottom: '0' });
        } else {
            $overlay.css({ top: '', left: '', right: '', bottom: '' });
        }
        $overlay.removeClass('hidden');
    };

    var hideLoadingOverlay = function (calendarView) {
        if (calendarView.$loadingOverlay)
            calendarView.$loadingOverlay.addClass('hidden');
    };

    var triggerCalendarItemEventWithWaitOverlay = function (calendarView, drop, eventName) {
        var eventData = drop.getEventDataFor(eventName);
        if (eventData && eventData.enabled && drop.piping && !_.get(drop, ['rootDrop', 'pointAndClickActive'], undefined)) {
            showLoadingOverlay(calendarView);
            var hideOverlay = _.once(function () {
                hideLoadingOverlay(calendarView);
            });
            var safetyTimer = window.setTimeout(hideOverlay, 60000);
            drop.triggerEvent(
                eventData.raisedEventName,
                undefined,
                undefined,
                undefined,
                false,
                null,
                function () {
                    window.clearTimeout(safetyTimer);
                    hideOverlay();
                }
            );
        } else {
            drop.triggerEventIfEnabled(eventName);
        }
    };

    return {

        fillUp: fillUp,
        showLoadingOverlay: showLoadingOverlay,
        hideLoadingOverlay: hideLoadingOverlay,

        timeslotMousedown: function ($weekday, dayOfWeek, e) {
            var calendarItemTemplate = _.find(this.childDrops, function (d) { return d.data && d.data.type === 'CalendarItem'; }) || this.childDrops[0];
            if (e.button !== 0 || $weekday === null || !!this.designData || !calendarItemTemplate || !calendarItemTemplate.data.enableDragAndResize || !calendarItemTemplate.data.eventing.onCreate || !calendarItemTemplate.data.eventing.onCreate.enabled) return;
            if ($(e.target).closest('.calendar-item').length) return;
            if (!this.designData)
                _.each(this.pool, function (drop) {
                    drop.$element
                        .css({
                            'cursor': 'default',
                            'pointer-events': 'none',
                            "opacity": 0.6
                        });
                });

            var dragStartMinutes = getMouseEventMinutes(e, this.data, false, this);

            var startOfDay = this.date.startOf('day').plus({ minutes: this.data.dayStart });
            var endOfDay = this.date.startOf('day').plus({ minutes: this.data.dayEnd });

            var itemData = {
                $target: $weekday,
                startOfDay: startOfDay,
                endOfDay: endOfDay,
                dayOfWeek: dayOfWeek,
                startOfItem: startOfDay.plus({ minutes: dragStartMinutes }),
                endOfItem: startOfDay.plus({ minutes: dragStartMinutes }),
                row: {},
                eventArg: this.data.eventArg,
                tooltipOutput: this.data.tooltipOutput,
                idOutput: this.data.idOutput
            };
            if (isPlanningView(this) && $weekday && $weekday.length) {
                var $planningGroup = $weekday.closest('.planning-group');
                if ($planningGroup.length) {
                    itemData.groupId = $planningGroup.data('groupId');
                }
            }
            var item = $.extend(true, {}, calendarItemTemplate, { data: itemData });

            var self = this;
            fillUp(this, [item]).then(function (dropArr) {
                var drop = dropArr[0];
                drop.applyPosition();
                drop.$element.css("pointer-events", "none");

                self.itemCreation = {
                    dragStartMinutes: dragStartMinutes,
                    inProgress: true,
                    isCreatable: false,
                    pageX: e.pageX,
                    pageY: e.pageY,
                    drop: drop
                };
            });
        },

        timeslotMouseup: function (e) {
            if (e.button !== 0 || !this.itemCreation || !this.itemCreation.inProgress) return;
            if (!this.designData)
                _.each(this.pool, function (drop) {
                    drop.$element
                        .css({
                            'cursor': 'all-scroll',
                            'pointer-events': '',
                            "opacity": 1
                        });
                });
            if (!this.itemCreation.isCreatable) {
                this.itemCreation.drop.$element.remove();
                delete this.itemCreation.drop;
            } else {
                this.itemCreation.drop.$element.css("pointer-events", "auto");

                var startMoment = this.itemCreation.drop.data.startOfItem;
                var endMoment = this.itemCreation.drop.data.endOfItem;

                if (startMoment.equals(endMoment)) {
                    endMoment.plus({ minutes: this.itemCreation.drop.data.resizeSnapInterval || this.data.timeslotSize });
                }
                var parameters = this.piping.parameters;
                const params = this.itemCreation.drop.data.createParams;
                if (params) {
                    if (this.getStartOfCurrentWeek) {
                        if (params.newStart) parameters.locals.setValue(params.newStart, calendarParameter.convert(this.getStartOfCurrentWeek().plus({ days: this.itemCreation.drop.data.dayOfWeek, minutes: startMoment })));
                        if (params.newEnd) parameters.locals.setValue(params.newEnd, calendarParameter.convert(this.getStartOfCurrentWeek().plus({ days: this.itemCreation.drop.data.dayOfWeek, minutes: endMoment })));
                    } else {
                        if (params.newStart) parameters.locals.setValue(params.newStart, calendarParameter.convert(startMoment));
                        if (params.newEnd) parameters.locals.setValue(params.newEnd, calendarParameter.convert(endMoment));
                    }
                    if (isPlanningView(this)) {
                        if (params.newGroup) parameters.locals.setValue(params.newGroup, this.itemCreation.drop.data.groupId);
                    }
                }

                triggerCalendarItemEventWithWaitOverlay(this, this.itemCreation.drop, 'onCreate');
            }
            this.itemCreation = {
                dragStartMinutes: null,
                inProgress: false,
                isCreatable: false,
                drop: null
            };
        },

        timeslotMousemove: function (e) {
            if (!this.itemCreation || !this.itemCreation.inProgress) return;
            var distance = Math.sqrt(Math.pow(e.pageX - this.itemCreation.pageX, 2) + Math.pow(e.pageY - this.itemCreation.pageY, 2));
            var timeslotHeight = getTimeslotHeight(this);
            if (distance > timeslotHeight / 3) {
                this.itemCreation.isCreatable = true;
            }

            var snapInterval = this.itemCreation.drop.data.resizeSnapInterval || this.data.timeslotSize;
            var dragMinutes = getMouseEventMinutes(e, this.data, true, this, snapInterval);

            var startOfDay = this.date.startOf('day').plus({ minutes: this.data.dayStart });

            var savedStart = this.itemCreation.drop.data.startOfItem;
            var savedEnd = this.itemCreation.drop.data.endOfItem;

            if (dragMinutes >= this.itemCreation.dragStartMinutes) {
                this.itemCreation.drop.data.startOfItem = startOfDay.plus({ minutes: this.itemCreation.dragStartMinutes });
                this.itemCreation.drop.data.endOfItem = startOfDay.plus({ minutes: dragMinutes });
            }
            else {
                this.itemCreation.drop.data.startOfItem = startOfDay.plus({ minutes: dragMinutes });
                this.itemCreation.drop.data.endOfItem = startOfDay.plus({ minutes: this.itemCreation.dragStartMinutes });
            }

            var overlapp = false;
            if (!this.itemCreation.drop.data.allowItemOverlap) {
                var currentStart = this.itemCreation.drop.data.startOfItem;
                var currentEnd = this.itemCreation.drop.data.endOfItem;

                if (this.getStartOfCurrentWeek) {
                    currentStart = this.getStartOfCurrentWeek().plus({ days: this.itemCreation.drop.data.dayOfWeek, minutes: this.itemCreation.drop.data.startOfItem });
                    currentEnd = this.getStartOfCurrentWeek().plus({ days: this.itemCreation.drop.data.dayOfWeek, minutes: this.itemCreation.drop.data.endOfItem });
                }

                var sameDayDrops = this.pool.filter(function (_drop) {
                    return _drop.data.startOfDay.startOf('day').equals(currentStart.startOf('day'));
                });

                currentStart = currentStart.plus({ minutes: this.itemCreation.drop.data.startOfItem });

                var currentInterval = Interval.fromDateTimes(currentStart, currentEnd);
                for (var i = 0;  i < sameDayDrops.length; i++) {
                    var sameDayDrop = sameDayDrops[i];
                    var isStartBetweenOrSame = currentInterval.contains(sameDayDrop.data.startOfItem) || sameDayDrop.data.startOfItem.equals(currentStart);
                    var isEndBetweenOrSame = currentInterval.contains(sameDayDrop.data.endOfItem) || sameDayDrop.data.endOfItem.equals(currentEnd);
                    if (isStartBetweenOrSame || isEndBetweenOrSame) {
                        overlapp = true;
                        break;
                    }
                };
            }

            if (!overlapp) {
                this.itemCreation.drop.applyPosition();
            } else {
                this.itemCreation.drop.data.startOfItem = savedStart;
                this.itemCreation.drop.data.endOfItem = savedEnd;
            }
        }

    };
});

